import { findVerificationDocument } from "@/lib/doctorVerificationDocuments";

const IDENTITY_OCR_KEYS = [
  "document_number",
  "documentNumber",
  "passport_number",
  "passport_no",
  "passportNo",
  "id_number",
  "id_no",
  "idNo",
  "number",
  "surname",
  "lastName",
  "last_name",
  "familyName",
  "family_name",
  "given_names",
  "givenNames",
  "firstName",
  "first_name",
  "names",
  "full_name",
  "fullName",
  "date_of_birth",
  "dateOfBirth",
  "dob",
  "birth_date",
  "birthDate",
  "birth_iso",
  "expiry_date",
  "expiryDate",
  "expiration_date",
  "expirationDate",
  "date_of_expiration",
  "dateOfExpiration",
  "dateOfExpiry",
  "expiry_dt",
  "expiryDateText",
  "expiration",
  "expiry_iso",
  "valid_until",
  "validUntil",
  "date_of_expiry",
  "exp_date",
  "expires_on",
  "expiry",
  "nationality",
  "sex",
];

function isRecord(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export function parseUploadNotes(notes) {
  if (!notes) return null;

  let current = notes;
  for (let i = 0; i < 3; i += 1) {
    if (isRecord(current)) return current;
    if (typeof current !== "string") return null;
    const trimmed = current.trim();
    if (!trimmed) return null;
    try {
      current = JSON.parse(trimmed);
    } catch {
      if (trimmed.startsWith("{")) {
        const firstLine = trimmed.split(/\r?\n/, 1)[0]?.trim();
        if (firstLine && firstLine !== trimmed) {
          try {
            current = JSON.parse(firstLine);
            continue;
          } catch {
            // Fall through to the null return below.
          }
        }
      }
      return null;
    }
  }

  return isRecord(current) ? current : null;
}

function collectIdentitySources(raw) {
  const root = parseUploadNotes(raw);
  if (!isRecord(root)) return [];

  const seen = new Set();
  const out = [];
  const push = (candidate) => {
    if (!isRecord(candidate) || seen.has(candidate)) return;
    seen.add(candidate);
    out.push(candidate);
  };

  push(root);
  push(root.fields);
  push(root.ocr_fields);
  push(root.ocrSummary?.fields);
  push(root.data);
  push(root.data?.fields);
  push(root.payload);
  push(root.payload?.fields);
  push(root.result);
  push(root.result?.fields);
  push(root.summary);
  push(root.summary?.fields);
  push(root.details);
  push(root.details?.summary);
  push(root.details?.summary?.fields);

  for (const key of ["fields", "ocr_fields", "ocrSummary", "data", "payload", "result", "summary", "details"]) {
    const parsed = parseUploadNotes(root?.[key]);
    if (!parsed) continue;
    push(parsed);
    push(parsed.fields);
    push(parsed.ocr_fields);
    push(parsed.ocrSummary?.fields);
    push(parsed.summary);
    push(parsed.summary?.fields);
    push(parsed.details);
    push(parsed.details?.summary);
    push(parsed.details?.summary?.fields);
  }

  return out;
}

export function sanitizeIdentityOcrFields(raw) {
  const sources = collectIdentitySources(raw);
  if (!sources.length) return {};

  const pick = (...keys) => {
    for (const key of keys) {
      for (const source of sources) {
        const candidate = source?.[key];
        if (candidate === undefined || candidate === null) continue;
        if (typeof candidate === "string" && !candidate.trim()) continue;
        return candidate;
      }
    }
    return null;
  };

  const next = {};
  const canonicalDocNumber = pick(
    "document_number",
    "documentNumber",
    "passport_number",
    "passport_no",
    "passportNo",
    "id_number",
    "id_no",
    "idNo",
    "number"
  );
  const canonicalSurname = pick("surname", "last_name", "lastName", "family_name", "familyName");
  const canonicalGivenNames = pick(
    "given_names",
    "givenNames",
    "first_name",
    "firstName",
    "names",
    "full_name",
    "fullName"
  );
  const canonicalDob = pick("date_of_birth", "dateOfBirth", "dob", "birth_date", "birthDate", "birth_iso");
  const canonicalExpiry = pick(
    "expiry_date",
    "expiryDate",
    "expiration_date",
    "expirationDate",
    "expiry_iso",
    "valid_until",
    "validUntil",
    "date_of_expiry",
    "exp_date",
    "expires_on",
    "expiry"
  );

  if (canonicalDocNumber) next.document_number = canonicalDocNumber;
  if (canonicalSurname) next.surname = canonicalSurname;
  if (canonicalGivenNames) next.given_names = canonicalGivenNames;
  if (canonicalDob) next.date_of_birth = canonicalDob;
  if (canonicalExpiry) next.expiry_date = canonicalExpiry;

  for (const key of IDENTITY_OCR_KEYS) {
    for (const source of sources) {
      if (source[key] !== undefined && source[key] !== null) {
        next[key] = source[key];
        break;
      }
    }
  }
  return next;
}

function splitIdentityName(fullName = "") {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return { givenNames: "", surname: "" };
  if (parts.length === 1) return { givenNames: "", surname: parts[0] };
  return {
    givenNames: parts.slice(0, -1).join(" "),
    surname: parts.slice(-1).join(" "),
  };
}

export function buildIdentityFallbackFields({
  rawFields,
  docValue,
  fullName,
  dateOfBirth,
  expiryDate,
}) {
  const base = sanitizeIdentityOcrFields(rawFields);
  const { givenNames, surname } = splitIdentityName(fullName);
  if (!base.document_number && docValue) base.document_number = docValue;
  if (!base.given_names && givenNames) base.given_names = givenNames;
  if (!base.surname && surname) base.surname = surname;
  if (!base.date_of_birth && dateOfBirth) base.date_of_birth = dateOfBirth;
  if (!base.expiry_date && expiryDate) base.expiry_date = expiryDate;
  return sanitizeIdentityOcrFields(base);
}

function selectLatestCheckWithOcrFields(rows = []) {
  const checks = Array.isArray(rows) ? rows : rows ? [rows] : [];
  for (const check of checks) {
    const fields = sanitizeIdentityOcrFields(check?.details);
    if (Object.keys(fields).length > 0) {
      return { check, fields };
    }
  }
  return {
    check: checks[0] || null,
    fields: {},
  };
}

export async function selectPatientIdentityHydration({ supabase, userId }) {
  const [profileRes, uploadRes, checkRes] = await Promise.all([
    supabase
      .from("patient_profiles")
      .select(
        "national_id, national_id_number, verification_status, full_name, first_name, last_name, date_of_birth"
      )
      .eq("user_id", userId)
      .maybeSingle(),
    supabase
      .from("id_uploads")
      .select("*")
      .eq("user_id", userId)
      .in("doc_type", ["passport", "national_id"])
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from("id_checks")
      .select("status,details,checked_at")
      .eq("user_id", userId)
      .order("checked_at", { ascending: false })
      .limit(50),
  ]);

  const profileData = profileRes?.data || null;
  const latestUpload = uploadRes?.data || null;
  const { check: latestCheck, fields: latestCheckFields } =
    selectLatestCheckWithOcrFields(checkRes?.data);
  const profileId = String(
    profileData?.national_id || profileData?.national_id_number || ""
  )
    .trim()
    .toUpperCase();
  const profileStatus = String(profileData?.verification_status || "")
    .trim()
    .toLowerCase();
  const latestStatus = String(latestUpload?.status || "").trim().toLowerCase();
  const latestFields = sanitizeIdentityOcrFields(parseUploadNotes(latestUpload?.notes));
  const docValue = String(
    latestCheckFields?.document_number ||
      latestFields?.document_number ||
      latestCheckFields?.id_number ||
      latestFields?.id_number ||
      latestCheckFields?.id_no ||
      latestFields?.id_no ||
      latestCheckFields?.passport_number ||
      latestFields?.passport_number ||
      profileId ||
      ""
  )
    .trim()
    .toUpperCase();
  const fullNameFallback =
    profileData?.full_name ||
    [profileData?.first_name, profileData?.last_name].filter(Boolean).join(" ");
  const hydratedFields = buildIdentityFallbackFields({
    rawFields: {
      ...(latestFields || {}),
      ...(latestCheckFields || {}),
    },
    docValue,
    fullName: fullNameFallback,
    dateOfBirth: profileData?.date_of_birth || "",
    expiryDate:
      latestCheckFields?.expiry_date ||
      latestFields?.expiry_date ||
      latestCheckFields?.expiry ||
      latestFields?.expiry ||
      latestCheckFields?.expiryDate ||
      latestFields?.expiryDate ||
      latestCheckFields?.expiration_date ||
      latestFields?.expiration_date ||
      latestCheckFields?.expirationDate ||
      latestFields?.expirationDate ||
      latestCheckFields?.expiry_iso ||
      latestFields?.expiry_iso ||
      latestCheckFields?.valid_until ||
      latestFields?.valid_until ||
      latestCheckFields?.validUntil ||
      latestFields?.validUntil ||
      latestCheckFields?.date_of_expiry ||
      latestFields?.date_of_expiry ||
      latestCheckFields?.exp_date ||
      latestFields?.exp_date ||
      latestCheckFields?.expires_on ||
      latestFields?.expires_on ||
      "",
  });

  return {
    profileData,
    profileId,
    profileStatus,
    latestUpload,
    latestCheck,
    latestStatus,
    latestFields,
    latestCheckFields,
    docValue,
    hydratedFields,
  };
}

export async function selectDoctorIdentityHydration({
  supabase,
  userId,
  authProfile = {},
  onboardingValue = "",
}) {
  const [profileRes, uploadRes, checkRes] = await Promise.all([
    supabase
      .from("doctor_profiles")
      .select("verification_documents,verified_status,full_name,first_name,last_name,date_of_birth")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase
      .from("id_uploads")
      .select("*")
      .eq("user_id", userId)
      .in("doc_type", ["passport", "national_id"])
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from("id_checks")
      .select("status,details,checked_at")
      .eq("user_id", userId)
      .order("checked_at", { ascending: false })
      .limit(50),
  ]);

  const profileData = profileRes?.data || null;
  const latestUpload = uploadRes?.data || null;
  const { check: latestCheck, fields: latestCheckFields } =
    selectLatestCheckWithOcrFields(checkRes?.data);
  const profileStatus = String(profileData?.verified_status || "")
    .trim()
    .toLowerCase();
  const latestStatus = String(latestUpload?.status || "").toLowerCase().trim();
  const latestFields = sanitizeIdentityOcrFields(parseUploadNotes(latestUpload?.notes));

  const passportDoc = findVerificationDocument(
    profileData?.verification_documents,
    "passport"
  );
  const nationalIdDoc = findVerificationDocument(
    profileData?.verification_documents,
    "national_id"
  );
  const doc = passportDoc || nationalIdDoc;
  const authDocValue = String(
    authProfile?.id_number ||
      authProfile?.national_id ||
      authProfile?.national_id_number ||
      ""
  )
    .trim()
    .toUpperCase();

  const docValue = String(
    doc?.id_value ||
      doc?.doc_number ||
      doc?.ocr_fields?.document_number ||
      latestCheckFields?.document_number ||
      latestFields?.document_number ||
      latestCheckFields?.id_number ||
      latestFields?.id_number ||
      latestCheckFields?.id_no ||
      latestFields?.id_no ||
      authDocValue ||
      onboardingValue ||
      ""
  )
    .trim()
    .toUpperCase();

  const fullNameFallback =
    profileData?.full_name ||
    [profileData?.first_name, profileData?.last_name].filter(Boolean).join(" ") ||
    authProfile?.full_name ||
    "";
  const dateOfBirthFallback =
    profileData?.date_of_birth || authProfile?.date_of_birth || "";
  const hydratedFields = buildIdentityFallbackFields({
    rawFields: {
      ...(doc?.ocr_fields || {}),
      ...(latestFields || {}),
      ...(latestCheckFields || {}),
    },
    docValue,
    fullName: fullNameFallback,
    dateOfBirth: dateOfBirthFallback,
    expiryDate:
      latestCheckFields?.expiry_date ||
      latestFields?.expiry_date ||
      latestCheckFields?.expiry ||
      latestFields?.expiry ||
      latestCheckFields?.expiryDate ||
      latestFields?.expiryDate ||
      latestCheckFields?.expiration_date ||
      latestFields?.expiration_date ||
      latestCheckFields?.expirationDate ||
      latestFields?.expirationDate ||
      latestCheckFields?.expiry_iso ||
      latestFields?.expiry_iso ||
      latestCheckFields?.valid_until ||
      latestFields?.valid_until ||
      latestCheckFields?.validUntil ||
      latestFields?.validUntil ||
      latestCheckFields?.date_of_expiry ||
      latestFields?.date_of_expiry ||
      latestCheckFields?.exp_date ||
      latestFields?.exp_date ||
      latestCheckFields?.expires_on ||
      latestFields?.expires_on ||
      doc?.ocr_fields?.expiry_date ||
      doc?.ocr_fields?.expiry ||
      doc?.ocr_fields?.expiryDate ||
      doc?.ocr_fields?.expiration_date ||
      doc?.ocr_fields?.expirationDate ||
      doc?.ocr_fields?.expiry_iso ||
      doc?.ocr_fields?.valid_until ||
      doc?.ocr_fields?.validUntil ||
      doc?.ocr_fields?.date_of_expiry ||
      doc?.ocr_fields?.exp_date ||
      doc?.ocr_fields?.expires_on ||
      doc?.expiry_date ||
      doc?.expiry ||
      doc?.expiryDate ||
      doc?.register_details?.expiryDate ||
      doc?.register_details?.expiry_date ||
      "",
  });

  return {
    profileData,
    profileStatus,
    latestUpload,
    latestCheck,
    latestStatus,
    latestFields,
    latestCheckFields,
    passportDoc,
    nationalIdDoc,
    doc,
    docValue,
    hydratedFields,
  };
}
